import { useEffect, useState } from "react";
import { api, API_BASE, dateFr } from "@/lib/rentApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileText, FileSignature, Download, Plus, Trash2, PenLine, ClipboardList, DoorOpen, DoorClosed, ImagePlus, GitCompare } from "lucide-react";
import { toast } from "sonner";

const STATUS_VARIANT = { brouillon: "secondary", partiellement_signé: "default", signé: "outline" };
const TYPE_META = {
  bail_meuble: { label: "Bail meublé", icon: FileText },
  inventaire: { label: "Inventaire mobilier", icon: ClipboardList },
  edl_entree: { label: "État des lieux d'entrée", icon: DoorOpen },
  edl_sortie: { label: "État des lieux de sortie", icon: DoorClosed },
};

export default function Documents() {
  const { user } = useAuth();
  const isOwner = user.role === "proprietaire" || user.role === "admin";
  const [leases, setLeases] = useState([]);
  const [leaseId, setLeaseId] = useState("");
  const [docs, setDocs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [signing, setSigning] = useState(null);
  const [comparing, setComparing] = useState(false);

  const loadDocs = (lid) => api.get("/documents", { params: lid ? { lease_id: lid } : {} }).then((r) => setDocs(r.data));

  useEffect(() => {
    if (isOwner) {
      api.get("/leases").then((r) => {
        setLeases(r.data);
        if (r.data[0]) { setLeaseId(r.data[0].id); loadDocs(r.data[0].id); } else setDocs([]);
      });
    } else loadDocs();
  }, []); // eslint-disable-line

  const onLease = (id) => { setLeaseId(id); setDocs(null); loadDocs(id); };
  const refresh = () => (isOwner ? loadDocs(leaseId) : loadDocs());

  const genBail = async () => {
    try { await api.post(`/leases/${leaseId}/bail`); toast.success("Bail généré"); loadDocs(leaseId); }
    catch { toast.error("Génération impossible"); }
  };
  const addDoc = async (type) => {
    try {
      await api.post("/documents", { lease_id: leaseId, type, data: type === "inventaire" ? { items: [] } : { rooms: [], meters: {}, cles: "" } });
      toast.success("Document créé"); loadDocs(leaseId);
    } catch { toast.error("Création impossible"); }
  };

  const downloadPdf = async (doc) => {
    try {
      const token = localStorage.getItem("rent_token");
      const res = await fetch(`${API_BASE}/documents/${doc.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${doc.type}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div>
      <PageHeader
        title="Documents locatifs"
        subtitle="Bail meublé officiel, inventaire, états des lieux (photos) — signés par OTP."
        action={isOwner && leases.length > 0 && (
          <Select value={leaseId} onValueChange={onLease}>
            <SelectTrigger className="w-64" data-testid="doc-lease-select"><SelectValue placeholder="Choisir un bail" /></SelectTrigger>
            <SelectContent>{leases.map((l) => <SelectItem key={l.id} value={l.id}>{l.tenant_name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      />

      {isOwner && leases.length === 0 ? (
        <EmptyState title="Aucun bail" desc="Créez d'abord un bail dans l'onglet Baux pour générer ses documents." />
      ) : (
        <>
          {isOwner && (
            <div className="flex flex-wrap gap-2 mb-6">
              <Button className="rounded-full gap-2" onClick={genBail} data-testid="gen-bail-btn"><FileText className="w-4 h-4" /> Générer le bail</Button>
              <Button variant="outline" className="rounded-full gap-2" onClick={() => addDoc("inventaire")} data-testid="add-inventaire-btn"><Plus className="w-4 h-4" /> Inventaire mobilier</Button>
              <Button variant="outline" className="rounded-full gap-2" onClick={() => addDoc("edl_entree")} data-testid="add-edl-entree-btn"><Plus className="w-4 h-4" /> État des lieux entrée</Button>
              <Button variant="outline" className="rounded-full gap-2" onClick={() => addDoc("edl_sortie")} data-testid="add-edl-sortie-btn"><Plus className="w-4 h-4" /> État des lieux sortie</Button>
              <Button variant="outline" className="rounded-full gap-2" onClick={() => setComparing(true)} data-testid="comparison-btn"><GitCompare className="w-4 h-4" /> Comparatif entrée/sortie</Button>
            </div>
          )}

          {docs === null ? <Loading /> : docs.length === 0 ? (
            <EmptyState title="Aucun document" desc={isOwner ? "Générez le bail ou ajoutez un inventaire / état des lieux." : "Vos documents à signer apparaîtront ici."} />
          ) : (
            <div className="space-y-4">
              {docs.map((doc) => {
                const meta = TYPE_META[doc.type] || { label: doc.type, icon: FileText };
                const Icon = meta.icon;
                return (
                  <div key={doc.id} className="p-5 rounded-xl border border-border bg-card" data-testid={`document-${doc.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Icon className="w-4 h-4 text-[hsl(var(--ring))]" />
                          <h3 className="font-heading font-semibold">{doc.title}</h3>
                          <Badge variant={STATUS_VARIANT[doc.status] || "secondary"}>{doc.status.replace("_", " ")}</Badge>
                          {(doc.photos?.length > 0) && <Badge variant="secondary">{doc.photos.length} photo(s)</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{meta.label} · {doc.property_address || doc.property_title}</p>
                        <div className="flex gap-3 mt-2 text-xs flex-wrap">
                          {(doc.required_parties || []).map((p) => {
                            const s = (doc.signatures || []).find((x) => x.party === p);
                            return <span key={p} className={s ? "text-[hsl(var(--chart-2))]" : "text-muted-foreground"}>{p} : {s ? `signé le ${dateFr(s.signed_at)}` : "en attente"}</span>;
                          })}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {isOwner && <Button size="sm" variant="ghost" onClick={() => setEditing(doc)} data-testid={`edit-doc-${doc.id}`}><PenLine className="w-4 h-4" /></Button>}
                        <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => downloadPdf(doc)} data-testid={`pdf-doc-${doc.id}`}><Download className="w-4 h-4" /> PDF</Button>
                        <Button size="sm" className="rounded-full gap-1" onClick={() => setSigning(doc)} data-testid={`sign-doc-${doc.id}`}><FileSignature className="w-4 h-4" /> Signer</Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {editing && <EditDialog doc={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {signing && <SignDialog doc={signing} role={user.role} onClose={() => setSigning(null)} onSigned={() => { setSigning(null); refresh(); }} />}
      {comparing && <ComparisonDialog leaseId={leaseId} onClose={() => setComparing(false)} />}
    </div>
  );
}

function Thumb({ docId, photo }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj;
    const token = localStorage.getItem("rent_token");
    fetch(`${API_BASE}/documents/${docId}/photos/${photo.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { obj = URL.createObjectURL(b); setUrl(obj); }).catch(() => {});
    return () => obj && URL.revokeObjectURL(obj);
  }, [docId, photo.id]);
  return (
    <div className="relative">
      {url ? <img src={url} alt={photo.filename} className="w-full h-24 object-cover rounded-lg border border-border" /> : <div className="w-full h-24 bg-secondary rounded-lg animate-pulse" />}
      <span className="block text-[10px] text-muted-foreground mt-1 truncate">{photo.room || "—"} · {dateFr(photo.uploaded_at)}</span>
    </div>
  );
}

function EditDialog({ doc, onClose, onSaved }) {
  const [data, setData] = useState(JSON.parse(JSON.stringify(doc.data || {})));
  const [photos, setPhotos] = useState(doc.photos || []);
  const [photoRoom, setPhotoRoom] = useState("");
  const isInv = doc.type === "inventaire";
  const isEdl = doc.type === "edl_entree" || doc.type === "edl_sortie";
  const isBail = doc.type === "bail_meuble";

  const save = async () => {
    try { await api.put(`/documents/${doc.id}`, { data }); toast.success("Enregistré"); onSaved(); }
    catch { toast.error("Erreur"); }
  };

  const addRow = () => {
    if (isInv) setData({ ...data, items: [...(data.items || []), { piece: "", objet: "", quantite: 1, etat: "Bon" }] });
    else setData({ ...data, rooms: [...(data.rooms || []), { piece: "", etat: "Bon", obs: "" }] });
  };
  const setRow = (i, k, v) => { const key = isInv ? "items" : "rooms"; const arr = [...data[key]]; arr[i] = { ...arr[i], [k]: v }; setData({ ...data, [key]: arr }); };
  const delRow = (i) => { const key = isInv ? "items" : "rooms"; setData({ ...data, [key]: data[key].filter((_, x) => x !== i) }); };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("room", photoRoom);
    try {
      const { data: ph } = await api.post(`/documents/${doc.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos([...photos, ph]); toast.success("Photo ajoutée");
    } catch { toast.error("Upload impossible"); }
    e.target.value = "";
  };

  // ----- clauses / annexes (bail)
  const setClause = (i, v) => { const arr = [...(data.clauses || [])]; arr[i] = v; setData({ ...data, clauses: arr }); };
  const addClause = () => setData({ ...data, clauses: [...(data.clauses || []), ""] });
  const delClause = (i) => setData({ ...data, clauses: (data.clauses || []).filter((_, x) => x !== i) });
  const toggleAnnexe = (i, v) => { const arr = [...(data.annexes || [])]; arr[i] = { ...arr[i], joint: v }; setData({ ...data, annexes: arr }); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{doc.title}</DialogTitle>
          <DialogDescription>Modifiez le contenu puis enregistrez.</DialogDescription>
        </DialogHeader>

        {isBail ? (
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Clauses particulières</Label>
              <div className="space-y-2 mt-2">
                {(data.clauses || []).map((c, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={c} onChange={(e) => setClause(i, e.target.value)} placeholder="Ex : interdiction de sous-louer" data-testid={`clause-${i}`} />
                    <Button size="icon" variant="ghost" className="text-muted-foreground" onClick={() => delClause(i)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="rounded-full gap-1" onClick={addClause} data-testid="add-clause-btn"><Plus className="w-4 h-4" /> Ajouter une clause</Button>
              </div>
            </div>
            <div>
              <Label className="font-semibold">Annexes & diagnostics</Label>
              <div className="space-y-2 mt-2">
                {(data.annexes || []).map((a, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={!!a.joint} onCheckedChange={(v) => toggleAnnexe(i, !!v)} data-testid={`annexe-${i}`} />
                    {a.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {(isInv ? data.items || [] : data.rooms || []).map((row, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                {isInv ? (
                  <>
                    <Input className="col-span-3" placeholder="Pièce" value={row.piece} onChange={(e) => setRow(i, "piece", e.target.value)} data-testid={`inv-piece-${i}`} />
                    <Input className="col-span-4" placeholder="Élément" value={row.objet} onChange={(e) => setRow(i, "objet", e.target.value)} data-testid={`inv-objet-${i}`} />
                    <Input className="col-span-2" type="number" placeholder="Qté" value={row.quantite} onChange={(e) => setRow(i, "quantite", e.target.value)} />
                    <Input className="col-span-2" placeholder="État" value={row.etat} onChange={(e) => setRow(i, "etat", e.target.value)} />
                  </>
                ) : (
                  <>
                    <Input className="col-span-3" placeholder="Pièce" value={row.piece} onChange={(e) => setRow(i, "piece", e.target.value)} data-testid={`edl-piece-${i}`} />
                    <Input className="col-span-3" placeholder="État" value={row.etat} onChange={(e) => setRow(i, "etat", e.target.value)} />
                    <Input className="col-span-5" placeholder="Observations" value={row.obs} onChange={(e) => setRow(i, "obs", e.target.value)} />
                  </>
                )}
                <Button size="icon" variant="ghost" className="col-span-1 text-muted-foreground" onClick={() => delRow(i)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-full gap-1" onClick={addRow} data-testid="add-row-btn"><Plus className="w-4 h-4" /> Ajouter une ligne</Button>

            {isEdl && (
              <>
                <div className="grid grid-cols-4 gap-2 pt-3 border-t border-border">
                  <div><Label className="text-xs">Compteur élec.</Label><Input value={data.meters?.electricite || ""} onChange={(e) => setData({ ...data, meters: { ...data.meters, electricite: e.target.value } })} /></div>
                  <div><Label className="text-xs">Compteur eau</Label><Input value={data.meters?.eau || ""} onChange={(e) => setData({ ...data, meters: { ...data.meters, eau: e.target.value } })} /></div>
                  <div><Label className="text-xs">Compteur gaz</Label><Input value={data.meters?.gaz || ""} onChange={(e) => setData({ ...data, meters: { ...data.meters, gaz: e.target.value } })} /></div>
                  <div><Label className="text-xs">Clés remises</Label><Input value={data.cles || ""} onChange={(e) => setData({ ...data, cles: e.target.value })} /></div>
                </div>
                <div className="pt-3 border-t border-border">
                  <Label className="font-semibold">Photos horodatées</Label>
                  <div className="flex items-end gap-2 mt-2">
                    <div className="flex-1"><Label className="text-xs">Pièce concernée</Label><Input value={photoRoom} onChange={(e) => setPhotoRoom(e.target.value)} placeholder="Ex : Cuisine" data-testid="photo-room" /></div>
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="photo-input" />
                      <span className="inline-flex items-center gap-1 rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary"><ImagePlus className="w-4 h-4" /> Ajouter</span>
                    </label>
                  </div>
                  {photos.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3">
                      {photos.map((ph) => <Thumb key={ph.id} docId={doc.id} photo={ph} />)}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter><Button className="rounded-full" onClick={save} data-testid="save-doc-btn">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignDialog({ doc, role, onClose, onSigned }) {
  const [party, setParty] = useState(role === "locataire" ? "locataire" : "bailleur");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [hint, setHint] = useState("");

  const request = async () => {
    if (!phone) return toast.error("Saisissez un numéro de téléphone");
    try {
      const { data } = await api.post(`/documents/${doc.id}/sign/request`, { party, phone });
      setStep(2);
      if (data.dev_code) setHint(`Mode démo — code SMS : ${data.dev_code}`);
      toast.success(data.message || "Code envoyé");
    } catch { toast.error("Envoi du code impossible"); }
  };
  const verify = async () => {
    try {
      await api.post(`/documents/${doc.id}/sign/verify`, { party, code, name });
      toast.success("Document signé électroniquement"); onSigned();
    } catch (e) { toast.error(e.response?.data?.detail || "Code invalide"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Signer — {doc.title}</DialogTitle>
          <DialogDescription>Signature électronique par code reçu par SMS.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Je signe en tant que</Label>
            <Select value={party} onValueChange={setParty}>
              <SelectTrigger data-testid="sign-party"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="bailleur">Bailleur</SelectItem><SelectItem value="locataire">Locataire</SelectItem></SelectContent>
            </Select>
          </div>
          {step === 1 ? (
            <>
              <div><Label>Nom du signataire</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="sign-name" /></div>
              <div><Label>Téléphone (pour le code SMS)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 6 12 34 56 78" data-testid="sign-phone" /></div>
              <Button className="w-full rounded-full" onClick={request} data-testid="sign-request-btn">Recevoir le code par SMS</Button>
            </>
          ) : (
            <>
              {hint && <p className="text-xs text-muted-foreground bg-accent/60 rounded p-2" data-testid="sign-hint">{hint}</p>}
              <div><Label>Code de signature (6 chiffres)</Label><Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} data-testid="sign-code" /></div>
              <Button className="w-full rounded-full" onClick={verify} data-testid="sign-verify-btn">Signer électroniquement</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ComparisonDialog({ leaseId, onClose }) {
  const [data, setData] = useState(null);
  const [ret, setRet] = useState({});

  useEffect(() => { api.get(`/leases/${leaseId}/edl-comparison`).then((r) => setData(r.data)).catch(() => setData({ entree_exists: false, sortie_exists: false, rows: [], deposit: 0 })); }, [leaseId]);

  const setR = (piece, k, v) => setRet((prev) => ({ ...prev, [piece]: { ...prev[piece], [k]: v } }));
  const total = Object.values(ret).reduce((s, r) => s + (Number(r?.montant) || 0), 0);
  const ready = data && data.entree_exists && data.sortie_exists;

  const download = async () => {
    const body = { retenues: Object.entries(ret).map(([piece, r]) => ({ piece, montant: Number(r.montant) || 0, motif: r.motif || "" })).filter((r) => r.montant > 0) };
    try {
      const token = localStorage.getItem("rent_token");
      const res = await fetch(`${API_BASE}/leases/${leaseId}/edl-comparison/pdf`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "comparatif_edl.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Génération impossible"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Comparatif état des lieux</DialogTitle>
          <DialogDescription>Différences entrée / sortie et chiffrage des retenues sur dépôt.</DialogDescription>
        </DialogHeader>
        {!data ? <Loading /> : !ready ? (
          <p className="text-sm text-muted-foreground py-6">Il faut un état des lieux d'<b>entrée</b> ET de <b>sortie</b> pour ce bail afin de générer le comparatif.</p>
        ) : (
          <div className="space-y-2">
            {data.rows.map((r, i) => (
              <div key={i} className={`p-3 rounded-lg border ${r.changed ? "border-[hsl(var(--chart-3))]" : "border-border"}`} data-testid={`comp-row-${i}`}>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{r.piece}</span>
                  <span className={r.changed ? "text-[hsl(var(--chart-3))]" : "text-muted-foreground"}>{r.changed ? "Différence" : "Identique"}</span>
                </div>
                <p className="text-xs text-muted-foreground">Entrée : {r.etat_entree} — Sortie : {r.etat_sortie}</p>
                {r.changed && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input placeholder="Motif de la retenue" onChange={(e) => setR(r.piece, "motif", e.target.value)} data-testid={`ret-motif-${i}`} />
                    <Input type="number" placeholder="Montant €" onChange={(e) => setR(r.piece, "montant", e.target.value)} data-testid={`ret-montant-${i}`} />
                  </div>
                )}
              </div>
            ))}
            <div className="pt-2 border-t border-border space-y-1 text-sm">
              <div className="flex justify-between"><span>Total des retenues</span><span className="font-semibold" data-testid="comp-total">{total.toFixed(2)} €</span></div>
              <div className="flex justify-between"><span>Dépôt de garantie</span><span>{Number(data.deposit || 0).toFixed(2)} €</span></div>
              <div className="flex justify-between"><span>Dépôt restitué au locataire</span><span className="font-semibold">{Math.max(Number(data.deposit || 0) - total, 0).toFixed(2)} €</span></div>
            </div>
          </div>
        )}
        <DialogFooter><Button className="rounded-full" onClick={download} disabled={!ready} data-testid="download-comparison-btn">Télécharger le PDF</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
